import { type TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useMutation, UseMutationOptions, UseMutationResult } from "@tanstack/react-query";
import { type ExecutionResult } from "graphql";
import { optInGqlx } from "shared/api/gqlx";
import { httpPostGraphql, StrictUndef } from "shared/api/httpClient";
import { useOptInUserFormContext } from "shared/components/opt-in/context";
import { useFormState } from "shared/hooks/useFormState";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";

type GraphqlMutationArgs<T, V> = {
    document: TypedDocumentNode<T, V>;
} & Omit<UseMutationOptions<StrictUndef<ExecutionResult<T>["data"]>, unknown, V>, "mutationFn">;

export function useGraphqlMutation<T, V>(document: GraphqlMutationArgs<T, V>["document"]): UseMutationResult<T, Error, V, unknown>;
export function useGraphqlMutation<T, V>(args: GraphqlMutationArgs<T, V>): UseMutationResult<T, Error, V, unknown>;
export function useGraphqlMutation<T, V>(args: GraphqlMutationArgs<T, V> | GraphqlMutationArgs<T, V>["document"]) {
    if ("document" in args) {
        const { document, onSuccess, onError, ...rest } = args;
        return useMutation({
            throwOnError: false,
            mutationFn: (variables) => httpPostGraphql<T, V>(document, variables),
            onSuccess: (res, ...rest) => {
                onSuccess?.(res, ...rest);
            },
            onError: (err, ...rest) => {
                if (typeof onError == "function") {
                    onError?.(err, ...rest);
                    return;
                }
                notify.show.error({ message: "Your request could not be processed" });
                logger.error(err);
            },
            ...rest,
        });
    } else {
        return useGraphqlMutation({ document: args });
    }
}
export function useOptInUserMutation<T, V>(
    document: GraphqlMutationArgs<T, V>["document"],
    userId?: string,
): UseMutationResult<T, Error, V, unknown>;
export function useOptInUserMutation<T, V>(args: GraphqlMutationArgs<T, V>, userId?: string): UseMutationResult<T, Error, V, unknown>;
export function useOptInUserMutation<T, V>(args: GraphqlMutationArgs<T, V> | GraphqlMutationArgs<T, V>["document"], userId?: string) {
    const form = useOptInUserFormContext();
    const [contextUserId] = useFormState(form, "id");

    if ("document" in args) {
        const { document, onSuccess, onError, ...rest } = args;

        return useMutation({
            throwOnError: false,
            mutationFn: (variables) => optInGqlx(userId ?? contextUserId)<T, V>(document, variables),
            onSuccess: (res, ...rest) => {
                onSuccess?.(res, ...rest);
            },
            onError: (err, ...rest) => {
                if (typeof onError == "function") {
                    onError?.(err, ...rest);
                    return;
                }
                notify.show.error({ message: "Your request could not be processed" });
                logger.error(err);
            },
            ...rest,
        });
    } else {
        return useOptInUserMutation({ document: args }, userId);
    }
}
